/*
    Taking a row out of the holdout report (MPN-1688, restyled for MPN-2046).

    A reason and a detail are both required, and the ticket says so: a picklist for the kind of
    problem, free text for what actually happened. The picklist alone would give an audit trail
    of "Insufficient data" repeated forty times, which answers nothing in six months.

    The confirm is deliberately not a browser dialog. This writes an audit record attributed to
    the person clicking it, and that deserves a form where the reason can be read back before
    it is committed.

    WHAT THE RESTYLE IS FOR. It was a bare Bootstrap modal that said "the campaign's data is not
    changed" in muted small print under the title -- the one sentence that decides whether
    somebody goes ahead. It is now the first thing in the dialog, stated as what will and will
    not happen, with the row being excluded named above it rather than folded into the
    paragraph. The destructive button still says what it does rather than "Confirm": a dialog
    whose primary action is "OK" is one people dismiss without reading.
*/
import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { excludeRow } from "@services/analytics/holdoutReportService";

import type { HoldoutRow } from "./holdoutReport";

const REASONS = [
    { value: "insufficient_data", label: "Insufficient data" },
    { value: "results_not_reliable", label: "Results not reliable" },
];

export default function ExcludeRowModal({ row, onClose, onDone }: {
    row: HoldoutRow; onClose: () => void; onDone: () => void;
}) {
    const [reason, setReason] = useState("");
    const [detail, setDetail] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const ready = Boolean(reason) && detail.trim().length > 0;

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            await excludeRow({
                launch_id: row.launch_id,
                journey_step_id: row.journey_step_id,
                is_excluded: true,
                reason,
                detail: detail.trim(),
            });
            onDone();
        } catch (e) {
            // Shown rather than logged. A refused exclusion that closes the dialog looks like
            // it worked, and the row would still be on the report tomorrow.
            setError(e instanceof Error ? e.message : "Could not exclude this row.");
            setSaving(false);
        }
    };

    return (
        <>
            <div className="modal d-block holdout-report" tabIndex={-1} role="dialog"
                style={{ zIndex: 1055 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content holdout-report__dialog">
                        <div className="holdout-report__dialog-head">
                            <span className="holdout-report__dialog-mark" aria-hidden="true">
                                <AlertTriangle size={16} />
                            </span>
                            <div>
                                <h5>Exclude from this report</h5>
                                <p>{row.campaign} &middot; {row.target_action}</p>
                            </div>
                        </div>

                        <div className="holdout-report__dialog-body">
                            {/* First, not in small print under the title. It is the sentence
                                that decides whether somebody goes ahead. */}
                            <div className="holdout-report__dialog-note">
                                <p>
                                    <strong>The campaign's data is not changed.</strong> This
                                    row leaves the report and every rollup and tile computed
                                    with it. The figures above move as a result.
                                </p>
                                <p>
                                    It is recorded against your name with the reason below, and
                                    anybody with access can restore it.
                                </p>
                            </div>

                            <label className="holdout-report__field-label" htmlFor="exclude-reason">
                                Reason
                            </label>
                            <select id="exclude-reason" className="form-select form-select-sm mb-3"
                                value={reason} onChange={(e) => setReason(e.target.value)}>
                                <option value="">Choose a reason...</option>
                                {REASONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>

                            <label className="holdout-report__field-label" htmlFor="exclude-detail">
                                Detail
                            </label>
                            <textarea id="exclude-detail" className="form-control form-control-sm"
                                rows={3}
                                placeholder="What is wrong with this row?"
                                value={detail} onChange={(e) => setDetail(e.target.value)} />
                            {/* Says why the box is required, where somebody is deciding how
                                much to type. A trail of "Insufficient data" forty times over
                                answers nothing in six months. */}
                            <p className="holdout-report__field-hint">
                                Read by whoever asks why this result is missing. The picklist
                                alone does not tell them.
                            </p>
                            {error && (
                                <div className="holdout-report__dialog-error" role="alert">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="holdout-report__dialog-foot">
                            <button type="button" className="holdout-report__chip-button"
                                onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="button" className="holdout-report__danger-button"
                                onClick={() => void submit()} disabled={!ready || saving}>
                                {saving ? "Excluding..." : "Exclude row"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop show" />
        </>
    );
}
